/**
 * Shell.tsx — layout autenticado da SPA: sidebar gradiente + conteúdo.
 *
 * Réplica da sidebar premium original (templates/includes/sidebar.html):
 * gradiente indigo→purple com blobs de glow, perfil com avatar em anel
 * gradiente, badge de papel, submenus expansíveis, item ativo com barra de
 * acento, e rodapé com toggle de modo escuro + Sair.
 *
 * Convivência (plano SPA, §3.2): domínio migrado → <Link> do react-router;
 * os demais → <a href> para as páginas Jinja (full page load é aceitável).
 */

import {
  faBalanceScale,
  faChevronDown,
  faCity,
  faCrown,
  faExchangeAlt,
  faFileAlt,
  faFileContract,
  faGavel,
  faHospitalUser,
  faKey,
  faList,
  faMoon,
  faSearch,
  faSearchDollar,
  faShieldAlt,
  faSignOutAlt,
  faSun,
  faTachometerAlt,
  faTasks,
  faUser,
  faUsersCog,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { logout, me, type Sessao } from '../api/auth'
import { ApiError } from '../api/http'
import { RastreadorProvider } from '../features/tasks/rastreador'
import { ChatbotWidget } from './ChatbotWidget'
import { useModoEscuro } from '../hooks/useModoEscuro'
import { temAlgumaPermissao, temPermissao } from '../lib/sessao'

interface SubItem {
  rotulo: string
  url: string
  icone: IconDefinition
  permissao?: string
}

interface ItemMenu {
  chave: string
  dominio: string // domínio da flag MIGRATED_DOMAINS
  rotulo: string
  rotaSpa: string
  urlJinja: string
  icone: IconDefinition
  ativo: string // gradiente da pílula quando ativo
  inativo: string // gradiente do badge quando inativo
  corIcone: string
  brilho: string
  permissoes?: string[] // exige ao menos uma
  soAdmin?: boolean // exige papel admin ou super_admin (fase Admin comum)
  submenu?: SubItem[]
  corSubmenu?: string
}

const MENU: ItemMenu[] = [
  {
    chave: 'dashboard', dominio: 'dashboard', rotulo: 'Dashboard',
    rotaSpa: '/dashboard', urlJinja: '/dashboard', icone: faTachometerAlt,
    ativo: 'from-indigo-600/80 to-purple-600/80',
    inativo: 'from-indigo-500/20 to-purple-500/20 group-hover:from-indigo-500/30 group-hover:to-purple-500/30',
    corIcone: 'text-indigo-300 group-hover:text-indigo-200', brilho: 'shadow-indigo-500/20',
  },
  {
    chave: 'processo', dominio: 'processos', rotulo: 'Processo Licitatório',
    rotaSpa: '/processos', urlJinja: '/processo-licitatorio', icone: faFileContract,
    ativo: 'from-blue-600/80 to-cyan-600/80',
    inativo: 'from-blue-500/20 to-cyan-500/20 group-hover:from-blue-500/30 group-hover:to-cyan-500/30',
    corIcone: 'text-blue-300 group-hover:text-blue-200', brilho: 'shadow-blue-500/20',
    permissoes: ['processo_licitatorio'], corSubmenu: 'border-blue-500/30',
    submenu: [
      { rotulo: 'Visão Geral', url: '/app/processos', icone: faList },
      { rotulo: 'DFD', url: '/app/dfd/novo', icone: faFileAlt },
    ],
  },
  {
    chave: 'auditoria', dominio: 'auditoria', rotulo: 'Auditoria',
    rotaSpa: '/auditoria', urlJinja: '/audit', icone: faSearchDollar,
    ativo: 'from-teal-600/80 to-emerald-600/80',
    inativo: 'from-teal-500/20 to-emerald-500/20 group-hover:from-teal-500/30 group-hover:to-emerald-500/30',
    corIcone: 'text-teal-300 group-hover:text-teal-200', brilho: 'shadow-teal-500/20',
    permissoes: ['auditoria'],
  },
  {
    chave: 'juridico', dominio: 'juridico', rotulo: 'Jurídico',
    rotaSpa: '/juridico', urlJinja: '/juridico', icone: faBalanceScale,
    ativo: 'from-purple-600/80 to-violet-600/80',
    inativo: 'from-purple-500/20 to-violet-500/20 group-hover:from-purple-500/30 group-hover:to-violet-500/30',
    corIcone: 'text-purple-300 group-hover:text-purple-200', brilho: 'shadow-purple-500/20',
    permissoes: ['pesquisa_juridica', 'impugnacao', 'defesa', 'auditoria_sus'],
    corSubmenu: 'border-purple-500/30',
    submenu: [
      { rotulo: 'Pesquisa Jurídica', url: '/legal_research', icone: faSearch, permissao: 'pesquisa_juridica' },
      { rotulo: 'Impugnação', url: '/juridico/impugnacao', icone: faGavel, permissao: 'impugnacao' },
      { rotulo: 'Gerar Defesa', url: '/juridico/defesa', icone: faShieldAlt, permissao: 'defesa' },
      { rotulo: 'Auditoria SUS', url: '/auditoria-sus/', icone: faHospitalUser, permissao: 'auditoria_sus' },
    ],
  },
  {
    chave: 'tasks', dominio: 'tasks', rotulo: 'Minhas Tarefas',
    rotaSpa: '/tarefas', urlJinja: '/tasks', icone: faTasks,
    ativo: 'from-amber-600/80 to-orange-600/80',
    inativo: 'from-amber-500/20 to-orange-500/20 group-hover:from-amber-500/30 group-hover:to-orange-500/30',
    corIcone: 'text-amber-300 group-hover:text-amber-200', brilho: 'shadow-amber-500/20',
  },
  {
    chave: 'admin', dominio: 'admin', rotulo: 'Administração',
    rotaSpa: '/admin/usuarios', urlJinja: '/admin', icone: faUsersCog,
    ativo: 'from-slate-600/80 to-indigo-600/80',
    inativo: 'from-slate-500/20 to-indigo-500/20 group-hover:from-slate-500/30 group-hover:to-indigo-500/30',
    corIcone: 'text-slate-300 group-hover:text-slate-200', brilho: 'shadow-slate-500/20',
    soAdmin: true,
  },
]

function Interior({ item, selecionado }: { item: ItemMenu; selecionado: boolean }) {
  return (
    <>
      {selecionado && (
        <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-white/70 to-white/40" />
      )}
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br transition-all duration-300 group-hover:scale-110 ${
          selecionado ? 'from-white/20 to-white/10' : item.inativo
        }`}
      >
        <FontAwesomeIcon icon={item.icone} className={selecionado ? 'text-white' : item.corIcone} />
      </div>
      <span
        className={`font-medium transition-colors ${
          selecionado ? 'text-white' : 'text-indigo-100 group-hover:text-white'
        }`}
      >
        {item.rotulo}
      </span>
    </>
  )
}

function ItemDeMenu({
  item, migrado, selecionado, expandido, aoExpandir, sessao,
}: {
  item: ItemMenu
  migrado: boolean
  selecionado: boolean
  expandido: boolean
  aoExpandir: () => void
  sessao: Sessao
}) {
  const classes = `group relative flex items-center gap-3 overflow-hidden rounded-xl px-4 py-3 transition-all duration-300 ${
    selecionado ? `bg-gradient-to-r shadow-lg ${item.ativo} ${item.brilho}` : 'hover:bg-white/5'
  }`

  if (item.submenu) {
    const subitens = item.submenu.filter(
      (s) => !s.permissao || temPermissao(sessao, s.permissao),
    )
    return (
      <div className="relative">
        <button onClick={aoExpandir} className={`${classes} w-full justify-between`}>
          <div className="flex items-center gap-3">
            <Interior item={item} selecionado={selecionado} />
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`text-xs transition-transform duration-300 ${expandido ? 'rotate-180' : ''} ${
              selecionado ? 'text-white/70' : 'text-indigo-400'
            }`}
          />
        </button>
        {expandido && (
          <div className={`ml-4 mt-1.5 space-y-1 border-l-2 pl-4 ${item.corSubmenu}`}>
            {subitens.map((s) => (
              <a
                key={s.rotulo}
                href={s.url}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-indigo-300 transition-all duration-200 hover:bg-white/5 hover:text-white"
              >
                <FontAwesomeIcon icon={s.icone} className="w-4 text-center text-xs" />
                <span>{s.rotulo}</span>
              </a>
            ))}
          </div>
        )}
      </div>
    )
  }

  return migrado ? (
    <Link to={item.rotaSpa} className={classes}>
      <Interior item={item} selecionado={selecionado} />
    </Link>
  ) : (
    <a href={item.urlJinja} className={classes}>
      <Interior item={item} selecionado={selecionado} />
    </a>
  )
}

export function Shell() {
  const localizacao = useLocation()
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { escuro, alternar } = useModoEscuro()
  const [expandido, setExpandido] = useState<string | null>(null)

  const { data: sessao, error, isLoading } = useQuery({
    queryKey: ['sessao'],
    queryFn: me,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  // Mesma regra do dashboard Jinja: super admin sem prefeitura escolhida vai
  // ao painel — vale para quem chega por link direto ou favorito, não só por
  // login. O painel agora é rota da SPA, então a ida é interna.
  const semPrefeitura =
    sessao?.usuario.papel === 'super_admin' && !sessao.tenant_ativo
  // Super admin que ASSUMIU uma prefeitura: precisa ver qual é e ter a
  // volta ao painel. O bloco existia na sidebar Jinja
  // (includes/sidebar.html) e não tinha sido portado — quem entrava numa
  // prefeitura ficava sem caminho de volta.
  const dentroDePrefeitura =
    sessao?.usuario.papel === 'super_admin' && Boolean(sessao.tenant_ativo)
  const noDashboard = localizacao.pathname === '/dashboard' ||
    localizacao.pathname === '/'

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-gray-500 dark:bg-dark-bg">
        Carregando…
      </div>
    )
  }
  if (error instanceof ApiError && error.codigo === 'NAO_AUTENTICADO') {
    return <Navigate to={`/login?next=${encodeURIComponent(localizacao.pathname)}`} replace />
  }
  if (error || !sessao) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-red-600 dark:bg-dark-bg">
        Erro ao carregar a sessão. Recarregue a página.
      </div>
    )
  }

  if (semPrefeitura && noDashboard) {
    return <Navigate to="/super-admin" replace />
  }

  const migrados = new Set(sessao.dominios_migrados)
  const ehAdmin = sessao.usuario.papel === 'admin' ||
    sessao.usuario.papel === 'super_admin'
  const visiveis = MENU.filter(
    (m) => (!m.permissoes || temAlgumaPermissao(sessao, ...m.permissoes)) &&
      (!m.soAdmin || ehAdmin),
  )

  async function sair() {
    await logout()
    clienteQuery.removeQueries({ queryKey: ['sessao'] })
    navegar('/login')
  }

  return (
    // Quem rola é o DOCUMENTO; a sidebar fica presa com `sticky top-0
    // h-screen`, então o rodapé (Modo Escuro/Sair) segue sempre visível.
    //
    // Antes isto era `h-screen overflow-hidden` com o <main> rolando por
    // dentro: quando uma regra do formulário removia um bloco, o conteúdo
    // encolhia, o scrollTop ficava além do novo scrollHeight e a área
    // abaixo só repintava quando o usuário mexia na tela.
    <div className="flex min-h-screen bg-slate-100 dark:bg-dark-bg">
      <nav className="sticky top-0 hidden h-screen w-72 flex-shrink-0 flex-col overflow-hidden bg-gradient-to-b from-indigo-700 via-indigo-800 to-purple-900 text-white md:flex">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-purple-500/10 blur-3xl" />
        </div>

        <div className="relative shrink-0 border-b border-white/10 bg-white/5 p-5 backdrop-blur-sm">
          <Link to="/dashboard" className="group flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 blur transition-opacity duration-300 group-hover:opacity-75" />
              <img
                src="/static/logo-removido.png"
                alt="LicitIA"
                className="relative h-12 w-12 rounded-xl shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div>
              <h1 className="bg-gradient-to-r from-white to-indigo-200 bg-clip-text text-xl font-bold text-transparent">
                LicitIA
              </h1>
              <p className="text-xs text-indigo-300/70">Inteligência Artificial</p>
            </div>
          </Link>
        </div>

        {/* min-h-0: sem isso o flex-item cresce em vez de rolar, e empurra
            o rodapé para fora da tela */}
        <div className="relative min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-4 text-center">
            <div className="relative mx-auto mb-3 h-16 w-16">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-75 blur-sm" />
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 shadow-xl">
                <FontAwesomeIcon icon={faUser} className="text-2xl text-white/90" />
              </div>
            </div>
            <h3 className="truncate text-sm font-semibold text-white">
              {sessao.usuario.nome ?? sessao.usuario.email}
            </h3>
            <div className="mt-1 flex items-center justify-center gap-1.5">
              {sessao.usuario.papel === 'super_admin' ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-yellow-400/30 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 px-2 py-0.5 text-xs text-yellow-300">
                  <FontAwesomeIcon icon={faCrown} className="text-[10px]" /> Super Admin
                </span>
              ) : sessao.usuario.papel === 'admin' ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-indigo-400/30 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 px-2 py-0.5 text-xs text-indigo-300">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-[10px]" /> Administrador
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-xs text-indigo-200">
                  <FontAwesomeIcon icon={faUser} className="text-[10px]" /> Usuário
                </span>
              )}
            </div>
          </div>

          {dentroDePrefeitura && (
            <div className="mb-3 rounded-xl border border-purple-400/30 bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-2.5">
              <p className="flex items-center justify-center gap-1 text-xs text-purple-200">
                <FontAwesomeIcon icon={faCity} />
                <span className="truncate">
                  {sessao.tenant_ativo_nome ?? 'Prefeitura selecionada'}
                </span>
              </p>
              <Link
                to="/super-admin"
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-yellow-300 transition-colors hover:text-yellow-200 hover:underline"
              >
                <FontAwesomeIcon icon={faExchangeAlt} />
                Voltar ao painel do super admin
              </Link>
            </div>
          )}

          <div className="mb-4 flex items-center gap-3 px-2">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-indigo-300/50">Menu</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </div>

          <div className="space-y-1.5">
            {visiveis.map((item) => (
              <ItemDeMenu
                key={item.chave}
                item={item}
                sessao={sessao}
                migrado={migrados.has(item.dominio)}
                selecionado={
                  migrados.has(item.dominio) &&
                  localizacao.pathname.startsWith(item.rotaSpa)
                }
                expandido={expandido === item.chave}
                aoExpandir={() =>
                  setExpandido((atual) => (atual === item.chave ? null : item.chave))
                }
              />
            ))}

            <a
              href="/change-password"
              className="group relative flex items-center gap-3 overflow-hidden rounded-xl px-4 py-3 transition-all duration-300 hover:bg-white/5"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-slate-500/20 to-slate-600/20 transition-all duration-300 group-hover:scale-110 group-hover:from-slate-500/30 group-hover:to-slate-600/30">
                <FontAwesomeIcon icon={faKey} className="text-slate-300 group-hover:text-slate-200" />
              </div>
              <span className="font-medium text-indigo-100 transition-colors group-hover:text-white">
                Alterar Senha
              </span>
            </a>
          </div>
        </div>

        {/* Rodapé fixo: shrink-0 impede que o menu (que rola) o comprima */}
        <div className="relative shrink-0 space-y-2 border-t border-white/10 bg-white/5 p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-700/50">
                <FontAwesomeIcon
                  icon={escuro ? faSun : faMoon}
                  className={`text-sm ${escuro ? 'text-yellow-300' : 'text-indigo-300'}`}
                />
              </div>
              <span className="text-sm text-indigo-200">
                {escuro ? 'Modo Claro' : 'Modo Escuro'}
              </span>
            </div>
            {/* pílula com sol/lua e knob deslizante (port do .dark-mode-toggle) */}
            <button
              onClick={alternar}
              aria-label="Alternar modo escuro"
              className={`relative h-7 w-14 shrink-0 overflow-hidden rounded-full transition-all duration-500 ${
                escuro
                  ? 'bg-gradient-to-br from-slate-800 to-slate-950'
                  : 'bg-gradient-to-br from-amber-400 to-amber-500'
              }`}
            >
              <FontAwesomeIcon
                icon={faSun}
                className={`absolute right-2 top-1/2 -translate-y-1/2 text-[12px] transition-opacity duration-500 ${
                  escuro ? 'text-white/40' : 'text-white'
                }`}
              />
              <FontAwesomeIcon
                icon={faMoon}
                className={`absolute left-2 top-1/2 -translate-y-1/2 text-[12px] transition-opacity duration-500 ${
                  escuro ? 'text-indigo-200' : 'text-white/40'
                }`}
              />
              <span
                className={`absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white shadow-md transition-transform duration-500 ${
                  escuro ? 'translate-x-[31px]' : 'translate-x-[3px]'
                }`}
              />
            </button>
          </div>

          <button
            onClick={() => void sair()}
            className="group flex w-full items-center gap-3 rounded-xl border border-red-500/30 bg-gradient-to-r from-red-500/20 to-rose-500/20 px-4 py-2.5 transition-all duration-300 hover:from-red-500/40 hover:to-rose-500/40"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/30 transition-all duration-300 group-hover:scale-110 group-hover:bg-red-500/50">
              <FontAwesomeIcon icon={faSignOutAlt} className="text-sm text-red-300 group-hover:text-red-200" />
            </div>
            <span className="text-sm text-red-200 transition-colors group-hover:text-white">Sair</span>
          </button>
        </div>
      </nav>

      {/* min-w-0: sem isso tabelas largas estouram o flex em vez de rolar */}
      <main className="min-w-0 flex-1 p-4 md:p-8">
        {semPrefeitura && (
          // Sem prefeitura escolhida o super admin não tem tenant em vigor:
          // as telas mostram só o que é dele. O painel (ainda Jinja) é onde
          // se escolhe — daí o link de página inteira.
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <span>
              <FontAwesomeIcon icon={faCrown} className="mr-2" />
              Nenhuma prefeitura selecionada — você está vendo apenas o que é
              seu.
            </span>
            <Link
              to="/super-admin"
              className="rounded-lg bg-amber-500 px-4 py-2 font-medium text-white transition-colors hover:bg-amber-600"
            >
              Escolher prefeitura
            </Link>
          </div>
        )}
        {/* O rastreador envolve o conteúdo: qualquer página pode registrar
            uma geração e o painel sobrevive à navegação (localStorage). */}
        <RastreadorProvider usuario={String(sessao.usuario.id)}>
          <Outlet context={sessao} />
          <ChatbotWidget />
        </RastreadorProvider>
      </main>
    </div>
  )
}
